import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Webhook, Save, Send, RefreshCw, KeyRound, Copy, Check, Loader2, AlertTriangle, ShieldCheck,
} from "lucide-react";

const errDetail = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fb;
};

/**
 * Per-seller tracking-webhook config form (GET/PUT /webhooks/tracking-config +
 * regenerate-secret + test). Used in BOTH portals: the seller portal renders it with
 * no sellerId (the seller manages their own, seller_id from the JWT); the admin portal
 * passes a sellerId (appended as ?seller_id= to manage that seller's config).
 *
 * @param sellerId optional seller id — admin mode when set, self-serve when null.
 */
export default function WebhookConfig({ sellerId = null }) {
  const q = sellerId ? `?seller_id=${encodeURIComponent(sellerId)}` : "";
  const base = `${API}/webhooks/tracking-config`;

  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [secretSet, setSecretSet] = useState(false);
  const [events, setEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState(null);
  const [copied, setCopied] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setRevealedSecret(null);
    setTestResult(null);
    try {
      const { data } = await axios.get(`${base}${q}`);
      setUrl(data.url || "");
      setEnabled(!!data.enabled);
      setSecretSet(!!data.secret_set);
      setEvents(data.events || []);
    } catch (e) {
      toast.error(errDetail(e, "Could not load the webhook config"));
    } finally {
      setLoading(false);
    }
  }, [base, q]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (enabled && !url.trim()) {
      toast.error("Enter an endpoint URL to enable the webhook.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.put(`${base}${q}`, { url: url.trim() || null, enabled });
      if (data.secret) {
        setRevealedSecret(data.secret);
        setSecretSet(true);
      } else if (data.secret_set != null) {
        setSecretSet(!!data.secret_set);
      }
      toast.success("Webhook settings saved.");
    } catch (e) {
      toast.error(errDetail(e, "Could not save the webhook settings"));
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await axios.post(`${base}/regenerate-secret${q}`);
      setRevealedSecret(data.secret);
      setSecretSet(true);
      toast.success("New signing secret generated — the previous one no longer verifies.");
    } catch (e) {
      toast.error(errDetail(e, "Could not regenerate the secret"));
    } finally {
      setRegenerating(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${base}/test${q}`);
      setTestResult(data);
      if (data.success) toast.success(`Test delivered — HTTP ${data.status_code}.`);
      else toast.error(`Your endpoint returned HTTP ${data.status_code}.`);
    } catch (e) {
      toast.error(errDetail(e, "Test delivery failed"));
    } finally {
      setTesting(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard?.writeText(revealedSecret);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="py-10 text-center text-sm text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading webhook config…
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Webhook className="w-4 h-4 text-brand" /> Tracking webhook
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-slate-500">
            EdgeLMS sends a signed JSON <span className="font-medium">POST</span> to your URL on every tracking
            update for your B2C orders. Verify the <code className="text-xs bg-slate-100 px-1 rounded">X-EdgeLMS-Signature</code>{" "}
            HMAC-SHA256 header with your signing secret.
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="wh-url" className="text-sm">Endpoint URL</Label>
            <Input id="wh-url" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://your-app.com/hooks/edgelms" data-testid="webhook-url" />
            <p className="text-xs text-slate-400">Must start with https:// (http:// allowed for testing).</p>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800">Enabled</p>
              <p className="text-xs text-slate-400">Turn delivery on or off without losing your settings.</p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="webhook-enabled" />
          </div>

          <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
            <div className="flex items-start gap-2 min-w-0">
              <KeyRound className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">Signing secret</p>
                <p className="text-xs text-slate-400">
                  {secretSet
                    ? "Configured — the value is shown only when (re)generated."
                    : "Created automatically on your first save."}
                </p>
              </div>
            </div>
            {secretSet && (
              <Button variant="outline" size="sm" onClick={regenerate} disabled={regenerating}>
                {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Regenerate</>}
              </Button>
            )}
          </div>

          {events.length > 0 && (
            <div>
              <Label className="text-xs text-slate-600">Events</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {events.map((e) => <Badge key={e} variant="outline" className="font-mono text-xs">{e}</Badge>)}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button onClick={save} disabled={saving} data-testid="webhook-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save
            </Button>
            <Button variant="outline" onClick={sendTest} disabled={testing || !url.trim()} data-testid="webhook-test">
              {testing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Send test
            </Button>
          </div>

          {testResult && (
            <div className={`rounded-lg border p-3 text-sm ${testResult.success ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
              <p className={`font-medium ${testResult.success ? "text-green-800" : "text-red-800"}`}>
                {testResult.success ? "Test delivered" : "Endpoint responded with an error"} — HTTP {testResult.status_code}
              </p>
              {testResult.response_preview && (
                <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-words text-xs text-slate-600">{testResult.response_preview}</pre>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {revealedSecret && (
        <Card className="border border-amber-200 bg-amber-50/60 shadow-sm">
          <CardContent className="p-4 space-y-2">
            <p className="text-sm font-medium text-amber-800 flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4" /> Your signing secret (shown once)
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded border border-amber-200 bg-white px-2 py-1.5 text-xs font-mono text-slate-800">{revealedSecret}</code>
              <Button size="sm" variant="outline" onClick={copySecret}>
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-amber-700 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Store this now — it won't be shown again. Use it to verify the HMAC-SHA256 signature of each delivery.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
